import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Lightbulb,
  Activity,
  Wallet,
  Clapperboard,
  MessageCircle,
  Gauge,
  BookOpen,
  Users,
  KanbanSquare,
  Truck,
  Images,
  CalendarDays,
  CalendarRange,
  Newspaper,
  Library,
  Network,
  Camera,
  Type,
  GraduationCap,
  Sparkles,
  LogOut,
  Palette,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

const items = [
  { to: "/", label: "Dziś", icon: LayoutDashboard },
  { to: "/podsumowanie", label: "Podsumowanie", icon: Lightbulb },
  { to: "/status", label: "Status", icon: Activity },
  { to: "/sprzedaz", label: "Sprzedaż", icon: Wallet },
  { to: "/filmy", label: "Filmy", icon: Clapperboard },
  { to: "/komentarze", label: "Komentarze", icon: MessageCircle },
  { to: "/barometr", label: "Barometr marki", icon: Gauge },
  { to: "/historie-messenger", label: "Historie Messenger", icon: BookOpen },
  { to: "/zespol-ai", label: "Zespół AI", icon: Users },
  { to: "/kanban", label: "Kanban", icon: KanbanSquare },
  { to: "/wysylka", label: "Wysyłka", icon: Truck },
  { to: "/karuzele", label: "Karuzele", icon: Images },
  { to: "/zdjecia", label: "Media", icon: Camera },
  { to: "/fonty", label: "Fonty", icon: Type },
  { to: "/plan-tygodnia", label: "Plan tygodnia", icon: CalendarDays },
  { to: "/tydzien-arka", label: "Tydzień Arka", icon: CalendarRange },
  { to: "/publikacje", label: "Publikacje", icon: Newspaper },
  { to: "/wiedza", label: "Wiedza", icon: Library },
  { to: "/przeplyw-wiedzy", label: "Przepływ wiedzy", icon: Network },
  { to: "/bart", label: "Bart", icon: GraduationCap },
];

const labItems = [{ to: "/op-demo", label: "OVL demo", icon: Palette }];

export default function Sidebar({
  mobileOpen = false,
  onNavigate,
}: {
  mobileOpen?: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    window.location.href = "/login";
  };

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border/50 bg-card/95 backdrop-blur-xl transition-transform duration-200 md:translate-x-0 md:bg-card/40",
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
      )}
    >
      <div className="flex items-center gap-3 px-6 py-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand to-brand/60 shadow-lg shadow-brand/30">
          <Sparkles className="h-5 w-5 text-brand-foreground" />
        </div>
        <div>
          <div className="text-sm font-semibold leading-tight">Kasia Zaręba</div>
          <div className="text-xs text-muted-foreground">Dashboard v2.0</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-3">
        {items.map(({ to, label, icon: Icon }) => {
          const active =
            to === "/karuzele" || to === "/komentarze"
              ? location.pathname.startsWith(to)
              : location.pathname === to;
          return (
            <NavLink
              key={to}
              to={to}
              onClick={() => onNavigate?.()}
              className={cn(
                "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                active
                  ? "text-foreground"
                  : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
              )}
            >
              {active && (
                <motion.div
                  layoutId="sidebar-active"
                  className="absolute inset-0 rounded-lg bg-accent"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}
              <Icon className="relative z-10 h-4 w-4" />
              <span className="relative z-10">{label}</span>
            </NavLink>
          );
        })}

        <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Lab
        </div>
        {labItems.map(({ to, label, icon: Icon }) => {
          const active = location.pathname === to;
          return (
            <NavLink
              key={to}
              to={to}
              onClick={() => onNavigate?.()}
              className={cn(
                "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                active
                  ? "text-foreground"
                  : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
              )}
            >
              {active && (
                <motion.div
                  layoutId="sidebar-active"
                  className="absolute inset-0 rounded-lg bg-accent"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}
              <Icon className="relative z-10 h-4 w-4" />
              <span className="relative z-10">{label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-border/50 p-3">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
          Wyloguj
        </button>
      </div>
    </aside>
  );
}
